import { supabase } from "./supabase.js";
import { store } from "./store.js";

// Dois canais Realtime multiplexados (não um por agente/quest) — ver design.md
// decisão 3. Cada callback atualiza a slice correspondente da store; os
// componentes reagem via subscribe, sem polling.

function applyAgentHealthRow(row) {
  store.agentHealth.set((map) => {
    const next = new Map(map);
    next.set(row.agent_name, {
      state: row.state,
      lastExecutionAt: row.last_execution_at,
      successRate24h: row.success_rate_24h,
      escalationRate24h: row.escalation_rate_24h,
      avgLatencyMs: row.avg_latency_ms,
      updatedAt: row.updated_at,
    });
    return next;
  });
}

function applyHumanApprovalRow(row, { removeIfResolved = false } = {}) {
  store.pendingQuests.set((map) => {
    const next = new Map(map);
    if (removeIfResolved && row.status !== "pending") {
      next.delete(row.id);
    } else {
      next.set(row.id, row);
    }
    return next;
  });
}

async function syncAgentHealth() {
  const { data, error } = await supabase.from("agent_health").select("*");
  if (error) {
    console.error("Falha ao sincronizar agent_health", error);
    return;
  }
  const map = new Map();
  for (const row of data) {
    map.set(row.agent_name, {
      state: row.state,
      lastExecutionAt: row.last_execution_at,
      successRate24h: row.success_rate_24h,
      escalationRate24h: row.escalation_rate_24h,
      avgLatencyMs: row.avg_latency_ms,
      updatedAt: row.updated_at,
    });
  }
  store.agentHealth.set(map);
}

async function syncPendingQuests() {
  const { data, error } = await supabase
    .from("human_approvals")
    .select("*")
    .eq("status", "pending")
    .order("created_at", { ascending: true });
  if (error) {
    console.error("Falha ao sincronizar human_approvals", error);
    return;
  }
  const map = new Map();
  for (const row of data) map.set(row.id, row);
  store.pendingQuests.set(map);
}

async function syncSystemAlerts() {
  const { data, error } = await supabase
    .from("system_alerts")
    .select("*")
    .eq("resolved", false)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("Falha ao sincronizar system_alerts", error);
    return;
  }
  store.systemAlerts.set(
    data.map((row) => ({
      id: row.id,
      severity: row.severity,
      component: row.component,
      message: row.message,
      createdAt: row.created_at,
    })),
  );
}

export async function startRealtime() {
  // Estado inicial via fetch direto — Realtime só entrega mudanças a partir daqui.
  await Promise.all([syncAgentHealth(), syncPendingQuests(), syncSystemAlerts()]);

  const agentHealthChannel = supabase
    .channel("dashboard-agent-health")
    .on("postgres_changes", { event: "*", schema: "public", table: "agent_health" }, (payload) => {
      if (payload.eventType === "DELETE") return;
      applyAgentHealthRow(payload.new);
      store.realtimeStatus.set((s) => ({ ...s, lastEventAt: Date.now() }));
    })
    .subscribe((status) => {
      store.realtimeStatus.set((s) => ({ ...s, agentHealth: status === "SUBSCRIBED" }));
      // Reconexão: ao voltar a SUBSCRIBED depois de queda, re-sincroniza para não
      // perder eventos que ocorreram durante a desconexão (ver spec: "Reconexão
      // após queda de rede").
      if (status === "SUBSCRIBED") syncAgentHealth();
    });

  // Sem filtro server-side por status: um filtro `status=eq.pending` é avaliado
  // contra o valor NOVO da linha, então um UPDATE que sai de pending (aprovado/
  // rejeitado por este ou outro dispositivo) nunca passaria no filtro e o card
  // nunca seria removido via Realtime nas outras sessões conectadas. Filtrar
  // client-side em applyHumanApprovalRow em vez disso.
  const humanApprovalsChannel = supabase
    .channel("dashboard-human-approvals")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "human_approvals" },
      (payload) => {
        if (payload.eventType === "DELETE") {
          store.pendingQuests.set((map) => {
            const next = new Map(map);
            next.delete(payload.old.id);
            return next;
          });
          return;
        }
        applyHumanApprovalRow(payload.new, { removeIfResolved: true });
      },
    )
    .subscribe((status) => {
      store.realtimeStatus.set((s) => ({ ...s, humanApprovals: status === "SUBSCRIBED" }));
      if (status === "SUBSCRIBED") syncPendingQuests();
    });

  const systemAlertsChannel = supabase
    .channel("dashboard-system-alerts")
    .on("postgres_changes", { event: "*", schema: "public", table: "system_alerts" }, () => {
      syncSystemAlerts();
    })
    .subscribe();

  return () => {
    supabase.removeChannel(agentHealthChannel);
    supabase.removeChannel(humanApprovalsChannel);
    supabase.removeChannel(systemAlertsChannel);
  };
}
